import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;
import { Subject, finalize, takeUntil } from 'rxjs';
import { RewardRule } from '../../../../../rewards/models/rewards-v2.models';
import { PackageService } from '../../../../../services/package.service';
import { RewardsService } from '../../../../../services/rewards.service';
import { EmptyResultComponent } from '../../../../../shared/components/empty-result/empty-result.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { RewardRuleCardComponent } from '../reward-rule-card/reward-rule-card.component';
import { extractErrorMessage } from '../../../../../shared/utils/error.util';

const DISABLE_REWARD_CONFIRMATION: ConfirmationModalData = {
  title: 'Disable Reward?',
  description: 'Are you sure you want to disable this reward? This action cannot be undone.',
  cancelText: 'Cancel',
  confirmText: 'Yes, Disable Reward',
  showTimeInfo: false,
  showCancelButton: true,
  showConfirmButton: true,
  isSuccessModal: false,
  isLoading: false,
};

@Component({
  selector: 'app-rewards-list-tab',
  standalone: true,
  imports: [
    CommonModule,
    EmptyResultComponent,
    RewardRuleCardComponent,
    ConfirmationModalComponent,
  ],
  templateUrl: './rewards-list-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-list-tab.component.scss'],
})
export class RewardsListTabComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() rewardRules: RewardRule[] = [];
  @Input() isLoadingRules = false;
  @Output() ruleDisabled = new EventEmitter<void>();
  @Output() editRule = new EventEmitter<RewardRule>();

  packageNameById = new Map<string, string>();
  displayRules: RewardRule[] = [];

  showDisableConfirmation = false;
  confirmationModalData: ConfirmationModalData = { ...DISABLE_REWARD_CONFIRMATION };
  ruleToDisable: RewardRule | null = null;

  readonly skeletonCards = [0, 1, 2, 3, 4, 5];

  constructor(
    private readonly packageService: PackageService,
    private readonly rewardsService: RewardsService,
    private readonly messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPackageNames();
    this.updateDisplayRules();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rewardRules']) {
      this.updateDisplayRules();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showEmptyState(): boolean {
    return !this.isLoadingRules && this.displayRules.length === 0;
  }

  get showGrid(): boolean {
    return !this.isLoadingRules && this.displayRules.length > 0;
  }

  trackByRuleId(_: number, rule: RewardRule): string {
    return rule.id;
  }

  onDisableRule(rule: RewardRule): void {
    this.ruleToDisable = rule;
    this.confirmationModalData = { ...DISABLE_REWARD_CONFIRMATION };
    this.showDisableConfirmation = true;
  }

  onDisableConfirmationCancel(): void {
    if (this.confirmationModalData.isLoading) {
      return;
    }
    this.showDisableConfirmation = false;
    this.ruleToDisable = null;
  }

  onDisableConfirmationConfirm(): void {
    if (!this.ruleToDisable || this.confirmationModalData.isLoading) {
      return;
    }
    this.deactivateRule(this.ruleToDisable.id);
  }

  private deactivateRule(ruleId: string): void {
    this.confirmationModalData = {
      ...this.confirmationModalData,
      isLoading: true,
    };

    this.rewardsService
      .deactivateRewardRule(ruleId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.confirmationModalData = {
            ...this.confirmationModalData,
            isLoading: false,
          };
        })
      )
      .subscribe({
        next: () => {
          this.showDisableConfirmation = false;
          this.ruleToDisable = null;
          this.ruleDisabled.emit();
        },
        error: (error: any) => {
          const message = extractErrorMessage(error, 'Failed to disable reward. Please try again.');
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
          });
        },
      });
  }

  private loadPackageNames(): void {
    this.packageService
      .getCarWashPackages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const packages = response.data ?? [];
          this.packageNameById = new Map(
            packages
              .filter(pkg => pkg.type === 'car_wash' && pkg.package_status)
              .map(pkg => [pkg.id, pkg.package_name])
          );
        },
        error: () => {
          this.packageNameById = new Map();
        },
      });
  }

  private updateDisplayRules(): void {
    this.displayRules = [...this.rewardRules].sort((a, b) => a.name.localeCompare(b.name));
  }
}
